import { useEffect } from 'react';
import {
  fmtAdv,
  fmtAdvInput,
  fmtDays,
  fmtDaysInput,
  fmtFloat,
  fmtFloatInput,
  fmtMcap,
  fmtMcapInput,
  fmtPrice,
  fmtPriceInput,
  parseAdv,
  parseDays,
  parseFloat_,
  parseMcap,
  parsePrice,
} from '../../config/constants/liquidityFilterData';
import { HistogramRangeFilter } from '../../components/EligibilityRules/HistogramRangeFilter';
import { FilterSection } from '../../components/EligibilityRules/FilterSection';
import { useStartingUniverseStore } from '../../store/useStartingUniverseStore';
import type { Filters } from '../../config/constants/liquidityFilterData';

// Section copy is hardcoded from Figma UniverseSelection.tsx lines
// 3741 / 3768 / 3794 / 4656 / 4967. The earlier locale lookups
// ("eligibility.liquidity.liquidity_filters", "price_range" etc.)
// were drifting from the Figma export — the design owner flagged
// the same misalignment elsewhere on this branch ("Single Security
// Restrictions" vs Figma's "Single Security Exclusions") and asked
// for i18n indirection to be dropped here.

// Placeholder histogram shape (relative bin counts) used when live security
// data is unavailable (local / CH down). The Figma renders mock distribution
// data so the chart and the Lin/Log toggle always appear; this gives the same
// always-populated look. Right-skewed, ~40 bins. In production the live bins
// replace it.
const SYNTHETIC_SHAPE: Array<number> = [
  3, 6, 11, 18, 28, 40, 54, 68, 80, 89, 94, 95, 92, 86, 78, 69, 60, 51, 43, 36,
  30, 25, 20, 17, 14, 11, 9, 8, 6, 5, 4, 4, 3, 2, 2, 2, 1, 1, 1, 1,
];

// The upper bound that means "no cap". The adapter emits `max: null` only for
// a value at or above the control's natural extent, so anything smaller — a
// data-derived maximum included — travels to the backend as a REAL cap.
const UNCAPPED = Number.MAX_SAFE_INTEGER;

interface DistributionData {
  bins: Array<number>;
  min: number;
  max: number;
  /** True data ceiling when the chart's own max is trimmed (see the price
   *  histogram's top-1% trim). Absent when chart and data agree. */
  fullMax?: number;
  /** 90th percentile of the field's values — the cap the "+ Max" button seeds. */
  p90?: number;
}

interface DistributionSet {
  price: DistributionData;
  adv: DistributionData;
  float: DistributionData;
  mcap: DistributionData;
  history: DistributionData;
}

interface ResolvedHistogram {
  distribution: Array<number>;
  min: number;
  max: number;
  fullMax: number | undefined;
  logDistribution: DistributionData | undefined;
  maxDefault: number | undefined;
}

// Use the live distribution when it has bins; otherwise fall back to the
// synthetic shape so the histogram + Lin/Log toggle render like the Figma.
// `logMin` (> 0) sets the log-axis floor so the toggle's data gate
// (max / min >= 100 in HistogramRangeFilter) is satisfied.
function resolveHistogram(
  linear: DistributionData,
  log: DistributionData,
  fallbackMin: number,
  fallbackMax: number,
  logMin: number,
  // Fixed "+ Max" seed for fields whose data p90 is a no-op cap. Float % piles
  // up at 100% (most stocks are fully free-floating), so its p90 == the ceiling
  // and seeding it does nothing — a literal cap is what makes "+ Max" useful.
  maxDefaultOverride?: number,
): ResolvedHistogram {
  if (linear.bins.length > 0) {
    return {
      distribution: linear.bins,
      min: Math.floor(linear.min),
      max: Math.ceil(linear.max),
      fullMax: linear.fullMax,
      maxDefault: maxDefaultOverride ?? linear.p90,
      logDistribution: log.bins.length > 0 ? log : undefined,
    };
  }
  return {
    distribution: SYNTHETIC_SHAPE,
    min: fallbackMin,
    max: fallbackMax,
    fullMax: undefined,
    maxDefault: maxDefaultOverride,
    logDistribution: {
      bins: SYNTHETIC_SHAPE,
      min: logMin,
      max: fallbackMax,
    },
  };
}

interface LiquidityInvestabilityProps {
  filters: Filters;
  update: <TKey extends keyof Filters>(key: TKey, value: Filters[TKey]) => void;
  distributions: {
    linear: DistributionSet;
    log: DistributionSet;
  };
  filterExcludedCounts: {
    liquidityGroupExcluded: number;
    priceExcluded: number;
    advExcluded: number;
    floatExcluded: number;
    marketCapExcluded: number;
    liquidityExcluded: number;
    sizeExcluded: number;
    historyExcluded: number;
  };
}

export function LiquidityInvestability({
  filters,
  update,
  distributions,
  filterExcludedCounts,
}: LiquidityInvestabilityProps) {
  // FilterSection rows are collapsible: `open` reads the store's per-section
  // expanded state (flipped by onToggle); they default open on first mount.
  const toggleSection = useStartingUniverseStore((s) => s.toggleFilterSection);
  const initSection = useStartingUniverseStore((s) => s.initFilterSection);
  const expandedSections = useStartingUniverseStore(
    (s) => s.expandedFilterSectionIds,
  );

  // Resolve each metric to live bins, or a synthetic fallback (no CH data) so
  // the chart + Lin/Log toggle always render like the Figma. fallback min/max
  // mirror the prior empty-state defaults; logMin keeps the log axis > 0.
  const price = resolveHistogram(
    distributions.linear.price,
    distributions.log.price,
    0,
    100,
    1,
  );
  const adv = resolveHistogram(
    distributions.linear.adv,
    distributions.log.adv,
    0,
    250_000_000,
    1_000,
  );
  const float = resolveHistogram(
    distributions.linear.float,
    distributions.log.float,
    0,
    100,
    1,
    // "+ Max" seeds a fixed 90% cap (not the data p90, which is 100% for float).
    90,
  );
  const mcap = resolveHistogram(
    distributions.linear.mcap,
    distributions.log.mcap,
    0,
    2_500_000_000,
    1_000_000,
  );
  const history = resolveHistogram(
    distributions.linear.history,
    distributions.log.history,
    0,
    720,
    1,
  );

  // Seed default open state on first mount.
  useEffect(() => {
    initSection('liquidity_investability', false);
    initSection('liquidity_filters', true);
    initSection('size_filters', true);
    initSection('history_filters', true);
  }, [initSection]);

  // Figma 3742-3751 — the outer wrapper carries the ONLY badge in this tree.
  // The three inner sections (Liquidity / Size / History) deliberately carry
  // none: with a per-slider count already on each filter, a section badge just
  // restated one of them ("Liquidity Filters 700" above "Security Price 700")
  // and added a third number to reconcile for no information.
  //
  // It is the UNION of the stocks the three inner filters remove, counted once
  // each. Summing the three inner counts happens to give the same answer today
  // only because they attribute each name to the FIRST sub-filter it fails and
  // then stop; the moment that attribution changes, the sum starts
  // double-counting names caught by several sliders. Read the union directly
  // so the badge can't drift from what it claims to measure.
  const totalExcluded = filterExcludedCounts.liquidityGroupExcluded;

  return (
    <div className="space-y-2.5">
      {/* Outer wrapper per Figma 3737 — three sibling filter groups
          nest INSIDE this card rather than render flat alongside it.
          Forced open here for the same reason as the inner rows. */}
      <FilterSection
        title="Liquidity & Investability"
        excludedCount={totalExcluded}
        open={expandedSections['liquidity_investability'] ?? false}
        onToggle={() => toggleSection('liquidity_investability')}
      >
        <div className="space-y-2.5">
          <FilterSection
            title="Liquidity Filters"
            open={expandedSections['liquidity_filters'] ?? true}
            onToggle={() => toggleSection('liquidity_filters')}
          >
            <HistogramRangeFilter
              // Figma 3794 — labelled "Security Price (USD)", not
              // "Price Range (USD)".
              label="Security Price (USD)"
              excludedCount={filterExcludedCounts.priceExcluded}
              distribution={price.distribution}
              min={price.min}
              max={price.max}
              step={1}
              value={filters.price}
              onChange={(v) => update('price', v)}
              formatAxis={fmtPrice}
              axisTicks={5}
              minLabel="Price Floor (USD)"
              maxLabel="Price Cap (USD)"
              formatInput={fmtPriceInput}
              parseInput={parsePrice}
              logDistribution={price.logDistribution}
              maxDefault={price.maxDefault}
              fullMax={price.fullMax}
              // Opening a filter must not invent an upper bound. Seeding the
              // observed maximum submitted it as a real cap the user never
              // typed — and it outlived the data it came from: open this on
              // US TME (max ≈ $6.2k), switch to Global Markets (max ≈ $116k),
              // and everything above $6.2k silently disappeared.
              defaultRange={[5, UNCAPPED]}
            />

            <HistogramRangeFilter
              label="20-Day Average Dollar Volume (USD)"
              excludedCount={filterExcludedCounts.advExcluded}
              distribution={adv.distribution}
              min={adv.min}
              max={adv.max}
              step={1_000_000}
              value={filters.adv}
              onChange={(v) => update('adv', v)}
              formatAxis={fmtAdv}
              axisTicks={5}
              minLabel="Min. ADV (USD)"
              maxLabel="Max. ADV (USD)"
              formatInput={fmtAdvInput}
              parseInput={parseAdv}
              logDistribution={adv.logDistribution}
              maxDefault={adv.maxDefault}
              defaultRange={[25_000_000, UNCAPPED]}
            />

            <HistogramRangeFilter
              label="Float %"
              excludedCount={filterExcludedCounts.floatExcluded}
              distribution={float.distribution}
              min={float.min}
              max={float.max}
              step={0.5}
              value={filters.float}
              onChange={(v) => update('float', v)}
              formatAxis={fmtFloat}
              axisTicks={5}
              minLabel="Min. Float (%)"
              maxLabel="Max. Float (%)"
              formatInput={fmtFloatInput}
              parseInput={parseFloat_}
              logDistribution={float.logDistribution}
              maxDefault={float.maxDefault}
              defaultRange={[45, UNCAPPED]}
              defaultLog={false}
              // Float % is physically 0–100%; a bound can't exceed 100 (a TYPE
              // rule, unlike price/ADV/mcap which are open-ended over history).
              valueMax={100}
            />
          </FilterSection>

          <FilterSection
            title="Size Filters"
            open={expandedSections['size_filters'] ?? true}
            onToggle={() => toggleSection('size_filters')}
          >
            <HistogramRangeFilter
              label="Market Cap (USD)"
              excludedCount={filterExcludedCounts.marketCapExcluded}
              distribution={mcap.distribution}
              min={mcap.min}
              max={mcap.max}
              step={10_000_000}
              value={filters.marketCap}
              onChange={(v) => update('marketCap', v)}
              formatAxis={fmtMcap}
              axisTicks={5}
              minLabel="Min. Market Cap (USD)"
              maxLabel="Max. Market Cap (USD)"
              formatInput={fmtMcapInput}
              parseInput={parseMcap}
              logDistribution={mcap.logDistribution}
              maxDefault={mcap.maxDefault}
              defaultRange={[300_000_000, UNCAPPED]}
            />
          </FilterSection>

          <FilterSection
            title="History Filters"
            open={expandedSections['history_filters'] ?? true}
            onToggle={() => toggleSection('history_filters')}
          >
            <HistogramRangeFilter
              label="Listing History (Days)"
              excludedCount={filterExcludedCounts.historyExcluded}
              distribution={history.distribution}
              min={history.min}
              max={history.max}
              step={1}
              value={filters.history}
              onChange={(v) => update('history', v)}
              formatAxis={fmtDays}
              axisTicks={5}
              minLabel="Min. Listing (Days)"
              maxLabel="Max. Listing (Days)"
              formatInput={fmtDaysInput}
              parseInput={parseDays}
              logDistribution={history.logDistribution}
              defaultRange={[180, UNCAPPED]}
              maxAvailable={false}
              defaultLog={false}
            />
          </FilterSection>
        </div>
      </FilterSection>
    </div>
  );
}
